import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  addGLeaderTicker,
  createOrder,
  deleteGLeaderTicker,
  getAccount,
  getAccountOrders,
  getAccountTickers,
  getAllAccounts,
  getGLeaderTickers,
  promoteGLeaderTicker,
  promoteGLeaderTickerToLeaders,
  updateGLeader,
  updateGLeaderTicker
} from '../services/api';
import './Watchlist.css';
import { Button, Modal, Form } from 'react-bootstrap';
import OrderDialog from '../components/OrderDialog';
import { FaEye, FaCheckCircle, FaArrowUp, FaStar } from 'react-icons/fa';
import { ThemeContext } from '../contexts/ThemeContext';
import TickerNotesPanel from '../components/TickerNotesPanel';
import { MAX_TICKER_LIST_SIZE } from '../utils/listLimits';
import StarRating from '../components/StarRating';

const VALID_SORT_COLUMNS = new Set(['ticker', 'notes', 'gain', 'gain_pct', 'status', 'rating', 'plan']);
const DEFAULT_SORT_COLUMN = 'status';
const SORT_COLUMN_STORAGE_KEY = 'gleader_details_sortColumn';
const SORT_DIRECTION_STORAGE_KEY = 'gleader_details_sortDirection';
const PLAN_OPTIONS = ['3%-1x', '5%-1x', '7%-1/2x', '9%-1/2x', '12%-1/4x', '15%-1/4x'];

const GLeaderDetails = () => {
  const {
    effectiveTheme,
    weatherThemeEnabled,
    selectedWeatherTheme,
  } = useContext(ThemeContext);
  const isDark = effectiveTheme === 'dark';
  const { themeName: themeNameParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const decodedThemeName = useMemo(() => decodeURIComponent(themeNameParam || ''), [themeNameParam]);
  const [themeDetails, setThemeDetails] = useState(() => {
    if (location.state?.themeName) {
      return { name: location.state.themeName, id: location.state.themeId };
    }
    return null;
  });
  const [themeLoadError, setThemeLoadError] = useState('');

  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tickerQuery, setTickerQuery] = useState('');
  const [addError, setAddError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [removingRowIds, setRemovingRowIds] = useState([]);
  const [deleteError, setDeleteError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [orderTicker, setOrderTicker] = useState(null);
  const [orderAccountId, setOrderAccountId] = useState('');
  const [orderDialogLoading, setOrderDialogLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountTickers, setSelectedAccountTickers] = useState([]);
  const [selectedAccountOrders, setSelectedAccountOrders] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTicker, setDeleteTicker] = useState(null);
  const [planUpdatingId, setPlanUpdatingId] = useState(null);
  const [planError, setPlanError] = useState('');
  const [planModalItem, setPlanModalItem] = useState(null);
  const [planModalValue, setPlanModalValue] = useState('');
  const [sortColumn, setSortColumn] = useState(() => {
    const saved = localStorage.getItem(SORT_COLUMN_STORAGE_KEY);
    const initialColumn = saved && VALID_SORT_COLUMNS.has(saved) ? saved : DEFAULT_SORT_COLUMN;
    if (!saved || saved !== initialColumn) {
      localStorage.setItem(SORT_COLUMN_STORAGE_KEY, initialColumn);
    }
    return initialColumn;
  });
  const [sortDirection, setSortDirection] = useState(() => {
    const saved = localStorage.getItem(SORT_DIRECTION_STORAGE_KEY);
    return saved || 'asc';
  });
  const [pinnedTickerId, setPinnedTickerId] = useState(null);
  // Error states
  const [promoteError, setPromoteError] = useState('');
  const [priceUpdateError, setPriceUpdateError] = useState('');
  const [globalPriceUpdateEnabled, setGlobalPriceUpdateEnabled] = useState(false);
  const [globalPriceUpdateLoading, setGlobalPriceUpdateLoading] = useState(false);
  const [isEditingSentiment, setIsEditingSentiment] = useState(false);
  const [sentimentDraft, setSentimentDraft] = useState('');
  const [sentimentSaving, setSentimentSaving] = useState(false);
  const [, setSentimentError] = useState('');
  const maxWatchlistSize = MAX_TICKER_LIST_SIZE;
  const removalTimersRef = useRef({});
  const ROW_REMOVE_ANIMATION_DURATION = 260;

  const focusTickerBySymbol = (symbol, sourceItems = watchlist) => {
    if (!symbol) return;
    const normalized = symbol.trim().toUpperCase();
    const target = sourceItems.find(item => item.ticker?.toUpperCase() === normalized);
    if (target) {
      setPinnedTickerId(target.id);
      setSelectedId(target.id);
      setHighlightedRowId(target.id);
    }
  };

  const fetchWatchlist = async ({ focusTicker } = {}) => {
    if (!decodedThemeName) {
      setThemeLoadError('Group name is missing from the URL.');
      setWatchlist([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setPriceUpdateError('');
    setThemeLoadError('');
    setPlanError('');
    try {
      const res = await getGLeaderTickers(decodedThemeName);
      const themeInfo = res.data?.theme;
      if (themeInfo) {
        setThemeDetails(themeInfo);
        setIsEditingSentiment(false);
      }
      if (res.data?.theme?.market_sentiment !== undefined) {
        setSentimentDraft(res.data.theme.market_sentiment || '');
      }
      let items = res.data?.items || [];

      const activeHoldingsNeedingUpdate = items.filter(
        item => item.is_purchased && item.include_in_price_updates === false
      );

      if (activeHoldingsNeedingUpdate.length > 0) {
        try {
          await Promise.all(
            activeHoldingsNeedingUpdate.map(item =>
              updateGLeaderTicker(decodedThemeName, item.id, { include_in_price_updates: true })
            )
          );
        } catch (enforceError) {
          console.error('Error enforcing price updates for active holdings:', enforceError);
          setPriceUpdateError(
            enforceError.response?.data?.error ||
            'Failed to enforce price updates for active holdings.'
          );
        } finally {
          items = items.map(item =>
            item.is_purchased ? { ...item, include_in_price_updates: true } : item
          );
        }
      } else {
        items = items.map(item =>
          item.is_purchased ? { ...item, include_in_price_updates: true } : item
        );
      }

      setWatchlist(items);
      if (focusTicker) {
        focusTickerBySymbol(focusTicker, items);
      } else if (items.length > 0 && !selectedId) {
        setSelectedId(items[0].id);
      }
    } catch (err) {
      setWatchlist([]);
      setThemeDetails(null);
      setThemeLoadError(err.response?.data?.error || 'Failed to load group tickers.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchWatchlist();
    getAllAccounts().then(res => setAccounts(res.data)).catch(() => setAccounts([]));
    // eslint-disable-next-line
  }, [decodedThemeName]);

  useEffect(() => {
    if (themeDetails) {
      setSentimentDraft(themeDetails.market_sentiment || '');
    }
  }, [themeDetails]);

  useEffect(() => {
    const timers = removalTimersRef.current;
    return () => {
      Object.values(timers).forEach(timerId => clearTimeout(timerId));
    };
  }, []);


  useEffect(() => {
    const nonActive = watchlist.filter(item => !item.is_purchased);
    if (nonActive.length === 0) {
      setGlobalPriceUpdateEnabled(false);
    } else {
      setGlobalPriceUpdateEnabled(nonActive.every(item => item.include_in_price_updates !== false));
    }
  }, [watchlist]);

  // Update ranks when watchlist changes (preserve user edits if possible)
  // Remove useEffect for ranks

  // Helper to get rank for a ticker id
  // Remove getRank

  // Handle rank change
  // Remove handleRankChange

  // Handle rank input blur/enter
  // Remove handleRankInput

  const handleAddTicker = async (e) => {
    e.preventDefault();
    if (!decodedThemeName) {
      setAddError('Group name is missing.');
      return;
    }
    if (watchlist.length >= maxWatchlistSize) {
      setAddError(`You cannot have more than ${maxWatchlistSize} tickers in this theme.`);
      return;
    }
    const normalizedTicker = tickerQuery.trim().toUpperCase();
    if (!normalizedTicker) return;
    setAddError('');
    try {
      await addGLeaderTicker(decodedThemeName, normalizedTicker);
      fetchWatchlist({ focusTicker: normalizedTicker });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to add ticker';
      setAddError(errorMessage);
      if (errorMessage === 'Ticker already exists for this theme') {
        focusTickerBySymbol(normalizedTicker);
      }
    }
  };

  const handleRemove = async (id) => {
    const tickerToDelete = watchlist.find(item => item.id === id);
    if (!tickerToDelete) return;

    const previousWatchlist = watchlist;
    const wasSelected = selectedId === id;

    setDeleteError('');
    setDeletingId(id);
    setRemovingRowIds(prev =>
      prev.includes(id) ? prev : [...prev, id]
    );

    const finalizeRemoval = () => {
      setWatchlist(prev => prev.filter(item => item.id !== id));
      if (wasSelected) {
        setSelectedId(null);
      }
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(current => (current === id ? null : current));
    };

    removalTimersRef.current[id] = setTimeout(() => {
      finalizeRemoval();
      delete removalTimersRef.current[id];
    }, ROW_REMOVE_ANIMATION_DURATION);

    try {
      await deleteGLeaderTicker(decodedThemeName, id);
    } catch (error) {
      console.error('Failed to delete theme ticker:', error);
      clearTimeout(removalTimersRef.current[id]);
      delete removalTimersRef.current[id];
      setWatchlist(previousWatchlist);
      if (wasSelected) {
        setSelectedId(id);
      }
      setDeleteError(
        error.response?.data?.error || 'Failed to delete ticker from theme.'
      );
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handlePromote = async (id) => {
    setDeletingId(id);
    setPromoteError('');
    try {
      await promoteGLeaderTicker(decodedThemeName, id);
      // If the promoted row was selected, select another
      if (selectedId === id) {
        setSelectedId(null);
      }
      fetchWatchlist();
    } catch (err) {
      console.error('Error promoting ticker:', err);
      const errorMessage = err.response?.data?.error || 'Failed to promote ticker';
      setPromoteError(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  const handlePromoteToLeaders = async (id) => {
    setDeletingId(id);
    setPromoteError('');
    try {
      await promoteGLeaderTickerToLeaders(decodedThemeName, id);
      // If the promoted row was selected, select another
      if (selectedId === id) {
        setSelectedId(null);
      }
      fetchWatchlist();
    } catch (err) {
      console.error('Error promoting ticker to leaders:', err);
      const errorMessage = err.response?.data?.error || 'Failed to promote ticker to leaders';
      setPromoteError(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  const handlePriceUpdateToggle = async (item, nextValue) => {
    if (item.is_purchased) {
      return;
    }
    if (globalPriceUpdateLoading) {
      return;
    }
    setPriceUpdateError('');
    const previousValue = item.include_in_price_updates !== false;

    setWatchlist(prev => prev.map(existing => (
      existing.id === item.id ? { ...existing, include_in_price_updates: nextValue } : existing
    )));

    try {
      const response = await updateGLeaderTicker(decodedThemeName, item.id, { include_in_price_updates: nextValue });
      const updatedItem = response.data;
      setWatchlist(prev => prev.map(existing => (
        existing.id === item.id
          ? (updatedItem.is_purchased
            ? { ...existing, ...updatedItem, include_in_price_updates: true }
            : { ...existing, ...updatedItem })
          : existing
      )));
    } catch (error) {
      console.error('Error updating price update setting:', error);
      setWatchlist(prev => prev.map(existing => (
        existing.id === item.id ? { ...existing, include_in_price_updates: previousValue } : existing
      )));
      setPriceUpdateError(error.response?.data?.error || 'Failed to update price update setting.');
    }
  };

  const handleGlobalPriceUpdateToggle = async (nextValue) => {
    if (globalPriceUpdateLoading) {
      return;
    }
    setPriceUpdateError('');
    const nonActiveItems = watchlist.filter(item => !item.is_purchased);
    if (nonActiveItems.length === 0) {
      return;
    }

    const itemsToUpdate = nonActiveItems.filter(
      item => (item.include_in_price_updates !== false) !== nextValue
    );

    const previousValues = itemsToUpdate.reduce((acc, item) => {
      acc[item.id] = item.include_in_price_updates !== false;
      return acc;
    }, {});

    setGlobalPriceUpdateLoading(true);

    setWatchlist(prev => prev.map(existing => (
      existing.is_purchased
        ? { ...existing, include_in_price_updates: true }
        : { ...existing, include_in_price_updates: nextValue }
    )));

    if (itemsToUpdate.length === 0) {
      setGlobalPriceUpdateLoading(false);
      return;
    }

    try {
      const responses = await Promise.all(
        itemsToUpdate.map(async item => {
          const response = await updateGLeaderTicker(decodedThemeName, item.id, { include_in_price_updates: nextValue });
          return response.data;
        })
      );

      const responseMap = new Map(responses.map(updated => [updated.id, updated]));

      setWatchlist(prev => prev.map(existing => {
        const updated = responseMap.get(existing.id);
        if (updated) {
          const merged = { ...existing, ...updated };
          return merged.is_purchased ? { ...merged, include_in_price_updates: true } : merged;
        }
        return existing.is_purchased ? { ...existing, include_in_price_updates: true } : existing;
      }));
    } catch (error) {
      console.error('Error updating price update setting:', error);
      setWatchlist(prev => prev.map(existing => {
        if (existing.is_purchased) {
          return { ...existing, include_in_price_updates: true };
        }
        if (Object.prototype.hasOwnProperty.call(previousValues, existing.id)) {
          return { ...existing, include_in_price_updates: previousValues[existing.id] };
        }
        return existing;
      }));
      setPriceUpdateError(error.response?.data?.error || 'Failed to update price update setting.');
    } finally {
      setGlobalPriceUpdateLoading(false);
    }
  };

  const handleSentimentSave = async () => {
    if (!themeDetails?.id) {
      setSentimentError('Group details are missing; cannot update sentiment.');
      return;
    }
    setSentimentSaving(true);
    setSentimentError('');
    try {
      const res = await updateGLeader(themeDetails.id, { market_sentiment: sentimentDraft });
      const updated = res.data || {};
      setThemeDetails(prev => ({ ...(prev || {}), ...updated }));
      setIsEditingSentiment(false);
    } catch (error) {
      console.error('Failed to update market sentiment:', error);
      setSentimentError(error.response?.data?.error || 'Failed to update market sentiment.');
    } finally {
      setSentimentSaving(false);
    }
  };

  const handleSentimentCancel = () => {
    setSentimentDraft(themeDetails?.market_sentiment || '');
    setIsEditingSentiment(false);
    setSentimentError('');
  };

  // Format gain as $1.00 or -$1.23, and gain % as 1.00% or -1.23%, with color
  const formatGainPct = (gainPct) => {
    if (gainPct === null || gainPct === undefined) return '-';
    const sign = gainPct > 0 ? '+' : gainPct < 0 ? '-' : '';
    return `${sign}${Math.abs(gainPct).toFixed(2)}%`;
  };
  const gainClass = (gain) => gain > 0 ? 'watchlist-gain-pos' : gain < 0 ? 'watchlist-gain-neg' : '';

  // Get seasonal background color with transparency
  const getSeasonalBackground = () => {
    if (!weatherThemeEnabled || !selectedWeatherTheme) {
      return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }
    
    const opacity = isDark ? 0.2 : 0.1; // Higher opacity for dark mode
    
    switch (selectedWeatherTheme) {
      case 'spring':
        return `rgba(40, 167, 69, ${opacity})`; // Green
      case 'summer':
        return `rgba(23, 162, 184, ${opacity})`; // Blue
      case 'autumn':
      case 'fall':
        return `rgba(255, 193, 7, ${opacity})`; // Orange
      case 'winter':
        return isDark ? 'rgba(248, 249, 250, 0.1)' : 'rgba(108, 117, 125, 0.1)'; // Gray
      default:
        return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }
  };

  const handleRatingChange = (tickerId, newRating) => {
    setWatchlist((prev) =>
      prev.map((item) =>
        item.ticker_id === tickerId ? { ...item, rating: newRating } : item
      )
    );
  };

  const handlePlanChange = async (item, newPlan) => {
    setPlanError('');
    const previousPlan = item.plan || '';
    const sanitizedPlan = newPlan || '';
    setPlanUpdatingId(item.id);
    setWatchlist((prev) =>
      prev.map((existing) =>
        existing.id === item.id ? { ...existing, plan: sanitizedPlan } : existing
      )
    );
    try {
      const res = await updateGLeaderTicker(decodedThemeName, item.id, { plan: sanitizedPlan || null });
      const updated = res.data;
      setWatchlist((prev) =>
        prev.map((existing) =>
          existing.id === item.id ? { ...existing, ...updated } : existing
        )
      );
    } catch (error) {
      console.error('Failed to update plan for theme ticker:', error);
      setPlanError(error.response?.data?.error || 'Failed to update plan.');
      setWatchlist((prev) =>
        prev.map((existing) =>
          existing.id === item.id ? { ...existing, plan: previousPlan } : existing
        )
      );
      return false;
    } finally {
      setPlanUpdatingId(null);
    }
    return true;
  };

  // Add rank to sort columns
  const handleSort = (column) => {
    let newDirection;
    if (sortColumn === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      setSortColumn(column);
      newDirection = 'asc';
      setSortDirection('asc');
    }

    // Persist to localStorage
    localStorage.setItem(SORT_COLUMN_STORAGE_KEY, column);
    localStorage.setItem(SORT_DIRECTION_STORAGE_KEY, newDirection);
  };

  const getSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Apply ticker filter
  const filteredWatchlist = tickerQuery
    ? watchlist.filter(item => item.ticker.toLowerCase().includes(tickerQuery.toLowerCase()))
    : watchlist;

  // Auto-select ticker when filter results in exactly one match
  useEffect(() => {
    if (tickerQuery && filteredWatchlist.length === 1) {
      setSelectedId(filteredWatchlist[0].id);
    }
  }, [tickerQuery, filteredWatchlist]);

  // Sort by rank for display (unless sorting by another column)
  const sortedWatchlist = [...filteredWatchlist].sort((a, b) => {
    let aValue, bValue;
    switch (sortColumn) {
      case 'ticker':
        aValue = a.ticker?.toUpperCase() || '';
        bValue = b.ticker?.toUpperCase() || '';
        break;
      case 'plan':
        aValue = a.plan?.toUpperCase() || '';
        bValue = b.plan?.toUpperCase() || '';
        break;
      case 'gain':
        aValue = a.gain ?? 0;
        bValue = b.gain ?? 0;
        break;
      case 'gain_pct':
        aValue = a.gain_pct ?? 0;
        bValue = b.gain_pct ?? 0;
        break;
      case 'status':
        aValue = a.is_purchased ? 1 : 0;
        bValue = b.is_purchased ? 1 : 0;
        break;
      case 'rating':
        aValue = a.rating ?? 0;
        bValue = b.rating ?? 0;
        break;
      case 'notes':
        // Sort by latest note update time, nulls last
        if (!a.latest_note_updated_at && !b.latest_note_updated_at) return 0;
        if (!a.latest_note_updated_at) return 1;
        if (!b.latest_note_updated_at) return -1;
        aValue = new Date(a.latest_note_updated_at).getTime();
        bValue = new Date(b.latest_note_updated_at).getTime();
        break;
      default:
        // Default to status sorting if no specific column is set
        aValue = a.is_purchased ? 1 : 0;
        bValue = b.is_purchased ? 1 : 0;
    }
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const prioritizedWatchlist = useMemo(() => {
    if (!pinnedTickerId) {
      return sortedWatchlist;
    }
    const targetIndex = sortedWatchlist.findIndex(item => item.id === pinnedTickerId);
    if (targetIndex <= 0) {
      return sortedWatchlist;
    }
    const targetItem = sortedWatchlist[targetIndex];
    return [
      targetItem,
      ...sortedWatchlist.slice(0, targetIndex),
      ...sortedWatchlist.slice(targetIndex + 1)
    ];
  }, [sortedWatchlist, pinnedTickerId]);

  const handleRowClick = (id) => {
    setSelectedId(id);
  };

  useEffect(() => {
    if (pinnedTickerId && !watchlist.some(item => item.id === pinnedTickerId)) {
      setPinnedTickerId(null);
    }
  }, [watchlist, pinnedTickerId]);

  const handleTickerClear = () => {
    setTickerQuery('');
    setPinnedTickerId(null);
    setAddError('');
  };




  // Create enhanced accounts array with full account data for the selected account
  const enhancedAccounts = accounts.map(acc => {
    if (acc.account_id === orderAccountId && selectedAccount) {
      return selectedAccount;
    }
    return acc;
  });

  const fetchAccountData = async (accountId) => {
    try {
      // Fetch account details, tickers, and orders
      const [accountRes, tickersRes, ordersRes] = await Promise.all([
        getAccount(accountId),
        getAccountTickers(accountId),
        getAccountOrders(accountId)
      ]);
      
      setSelectedAccount(accountRes.data);
      setSelectedAccountTickers(tickersRes.data);
      setSelectedAccountOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching account data:', error);
      setSelectedAccount(null);
      setSelectedAccountTickers([]);
      setSelectedAccountOrders([]);
    }
  };

  const handleAccountChange = async (newAccountId) => {
    setOrderAccountId(newAccountId);
    if (newAccountId) {
      await fetchAccountData(newAccountId);
    } else {
      setSelectedAccount(null);
      setSelectedAccountTickers([]);
      setSelectedAccountOrders([]);
    }
  };
  const handleOrderDialogClose = () => {
    setShowOrderDialog(false);
    setOrderTicker(null);
    setOrderAccountId('');
    setSelectedAccount(null);
    setSelectedAccountTickers([]);
    setSelectedAccountOrders([]);
  };
  const handleOrderSave = async (orderData) => {
    setOrderDialogLoading(true);
    try {
      await createOrder(orderAccountId, orderData);
      setShowOrderDialog(false);
      setOrderTicker(null);
      setOrderAccountId('');
      setSelectedAccount(null);
      setSelectedAccountTickers([]);
      setSelectedAccountOrders([]);
      // Optionally refresh data
    } catch (error) {
      console.error('Error creating order:', error);
      // You might want to show an error message to the user here
    } finally {
      setOrderDialogLoading(false);
    }
  };

  // Calculate available cash after existing orders
  const nonDeletedOrderTotal = selectedAccountOrders.filter(o => o.status !== 'DELETED' && o.status !== 'FULFILLED').reduce((sum, o) => sum + (Number(o.price) * Number(o.quantity)), 0);
  const availableCashAfterOrders = (selectedAccount?.cash || 0) - nonDeletedOrderTotal;
  const handleDeleteConfirm = async () => {
    if (deleteTicker) {
      const tickerToDelete = deleteTicker;
      const previousWatchlist = watchlist;
      const wasSelected = selectedId === tickerToDelete.id;

      setShowDeleteConfirm(false);
      setDeleteTicker(null);
      setDeletingId(tickerToDelete.id);
      setRemovingRowIds(prev =>
        prev.includes(tickerToDelete.id) ? prev : [...prev, tickerToDelete.id]
      );

      const finalizeRemoval = () => {
        setWatchlist(prev => prev.filter(item => item.id !== tickerToDelete.id));
        if (wasSelected) {
          setSelectedId(null);
        }
        setRemovingRowIds(prev => prev.filter(id => id !== tickerToDelete.id));
        setHighlightedRowId(current => (current === tickerToDelete.id ? null : current));
      };

      removalTimersRef.current[tickerToDelete.id] = setTimeout(() => {
        finalizeRemoval();
        delete removalTimersRef.current[tickerToDelete.id];
      }, ROW_REMOVE_ANIMATION_DURATION);

      try {
        await deleteGLeaderTicker(decodedThemeName, tickerToDelete.id);
      } catch (error) {
        console.error('Failed to delete theme ticker:', error);
        clearTimeout(removalTimersRef.current[tickerToDelete.id]);
        delete removalTimersRef.current[tickerToDelete.id];
        setWatchlist(previousWatchlist);
        if (wasSelected) {
          setSelectedId(tickerToDelete.id);
        }
        setDeleteError(
          error.response?.data?.error || 'Failed to delete ticker from theme.'
        );
        setRemovingRowIds(prev => prev.filter(id => id !== tickerToDelete.id));
        setHighlightedRowId(tickerToDelete.id);
      } finally {
        setDeletingId(null);
      }
    }
  };
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteTicker(null);
    setHighlightedRowId(null);
  };

  // Helper function to get ticker ID for a watchlist item
  const getTickerIdForWatchlistItem = (watchlistItem) => {
    // The ticker_id is now included in the watchlist item data from the backend
    return watchlistItem.ticker_id;
  };

  const nonActiveWatchlistCount = watchlist.filter(item => !item.is_purchased).length;

  return (
    <div className="container-xl" style={{ '--seasonal-bg': getSeasonalBackground() }}>
      <div className="d-flex flex-wrap align-items-center mb-3 gap-2">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-1">{themeDetails?.name || decodedThemeName || 'Group'}</h4>
          <Button variant="outline-secondary" size="sm" onClick={() => navigate('/gleaders')}>
            Back to GLeaders
          </Button>
        </div>
      </div>
      <div className="d-flex flex-wrap align-items-center mb-3 gap-2">
        <div className="flex-grow-1">
          {isEditingSentiment ? (
            <div className="d-flex align-items-start gap-2 w-100">
              <Form.Control
                as="textarea"
                rows={3}
                value={sentimentDraft}
                onChange={(e) => setSentimentDraft(e.target.value)}
                placeholder="Market sentiment"
                className="flex-grow-1"
                style={{ fontSize: '1rem' }}
              />
              <div className="d-flex flex-column gap-2">
                <Button
                  variant="link"
                  size="sm"
                  className="text-success p-0"
                  onClick={handleSentimentSave}
                  disabled={sentimentSaving}
                  title="Save sentiment"
                  aria-label="Save sentiment"
                >
                  {sentimentSaving ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : <i className="bi bi-check-lg"></i>}
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="text-muted p-0"
                  onClick={handleSentimentCancel}
                  disabled={sentimentSaving}
                  title="Cancel editing sentiment"
                  aria-label="Cancel editing sentiment"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2 flex-nowrap">
              <div
                className="text-muted flex-grow-1"
                style={{ whiteSpace: 'pre-wrap', flexBasis: '100%', fontSize: '1rem' }}
              >
                {themeDetails?.market_sentiment || 'No market sentiment yet.'}
              </div>
              <Button
                variant="link"
                size="sm"
                className="text-muted p-0"
                onClick={() => {
                  setIsEditingSentiment(true);
                  setSentimentError('');
                }}
                title="Edit market sentiment"
                aria-label="Edit market sentiment"
              >
                <i className="bi bi-pencil"></i>
              </Button>
            </div>
          )}
        </div>
      </div>
      {themeLoadError && <div className="alert alert-danger py-2">{themeLoadError}</div>}
      <div className="watchlist-2col-layout">
        <div className="watchlist-table-col">
          <form className="watchlist-add-form" onSubmit={handleAddTicker}>
            <div className="watchlist-input-stack">
              <div className="watchlist-input-wrapper">
                <input
                  type="text"
                  className="watchlist-input"
                  placeholder="Filter or add ticker (e.g. AAPL)"
                  value={tickerQuery}
                  onChange={e => setTickerQuery(e.target.value)}
                  maxLength={16}
                />
              </div>
              <button
                type="button"
                className="watchlist-action-btn watchlist-clear-btn"
                onClick={handleTickerClear}
                disabled={!tickerQuery}
              >
                Clear
              </button>
              <button
                type="submit"
                className="watchlist-action-btn watchlist-add-btn"
                disabled={watchlist.length >= maxWatchlistSize}
              >
                Add
              </button>
            </div>
            <Form.Check
              type="switch"
              id="watchlist-global-price-toggle"
              className="price-update-toggle"
              checked={globalPriceUpdateEnabled}
              disabled={globalPriceUpdateLoading || nonActiveWatchlistCount === 0}
              onChange={e => handleGlobalPriceUpdateToggle(e.target.checked)}
              title={
                nonActiveWatchlistCount === 0
                  ? 'All tickers in this theme are active holdings and already receive price updates.'
                  : 'Toggle price updates for every non-holding ticker in this theme.'
              }
              label={
                <div className="two-line-label">
                  <span>Price updates</span>
                  <span>(non-holdings)</span>
                </div>
              }
            />
            {addError && <span className="watchlist-error">{addError}</span>}
            {promoteError && <span className="watchlist-error">{promoteError}</span>}
            {deleteError && <span className="watchlist-error">{deleteError}</span>}
            {priceUpdateError && <span className="watchlist-error">{priceUpdateError}</span>}
            {planError && <span className="watchlist-error">{planError}</span>}
            {watchlist.length >= maxWatchlistSize && (
              <span className="watchlist-error">Maximum of {maxWatchlistSize} tickers reached.</span>
            )}
          </form>
          <div className="tickers-table-container">
            <div className="table-responsive">
              <table className="ticker-table tickers-table" style={{ tableLayout: 'auto', width: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: '40px' }}>#</th>
                    <th style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('ticker')}>
                      Ticker{getSortIndicator('ticker')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '80px' }} onClick={() => handleSort('notes')}>
                      Notes{getSortIndicator('notes')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '100px' }} onClick={() => handleSort('rating')}>
                      Rating{getSortIndicator('rating')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '138px' }} onClick={() => handleSort('plan')}>
                      Plan{getSortIndicator('plan')}
                    </th>
                    <th className="text-end" style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('gain_pct')}>
                      Gain %{getSortIndicator('gain_pct')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('status')}>
                      Status{getSortIndicator('status')}
                    </th>
                    <th className="text-center" style={{ width: '90px' }}>Update</th>
                    <th className="text-end" style={{ width: '180px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9}>Loading...</td></tr>
                  ) : prioritizedWatchlist.length === 0 ? (
                    <tr><td colSpan={9}>No tickers in this theme.</td></tr>
                  ) : (
                    <>
                      {prioritizedWatchlist.map((item, index) => (
                        <tr
                          key={item.id}
                          className={`watchlist-row${selectedId === item.id ? ' watchlist-row-selected' : ''}${highlightedRowId === item.id ? ' watchlist-row-highlight' : ''}${removingRowIds.includes(item.id) ? ' watchlist-row-removing' : ''}`}
                          onClick={() => handleRowClick(item.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="text-center" style={{ width: '40px', fontSize: '0.85rem', color: '#888' }}>{index + 1}</td>
                          <td style={{ width: '60px' }}>{item.ticker}</td>
                          <td className="text-center" style={{ width: '80px', fontSize: '0.75rem' }}>
                            {item.latest_note_updated_at ? (() => {
                              const noteDate = new Date(item.latest_note_updated_at);
                              const today = new Date();
                              const isToday = noteDate.toDateString() === today.toDateString();
                              const timeStr = noteDate.toLocaleDateString('en-US', {
                                month: 'numeric',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: false
                              }).replace(',', '');

                              return isToday ? (
                                <span className="badge bg-success" style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>
                                  {timeStr}
                                </span>
                              ) : timeStr;
                            })() : '-'}
                          </td>
                          <td className="text-center" style={{ width: '100px' }} onClick={e => e.stopPropagation()}>
                            {item.ticker_id ? (
                              <StarRating
                                tickerId={item.ticker_id}
                                symbol={item.ticker}
                                currentRating={item.rating}
                                onRatingChange={(newRating) => handleRatingChange(item.ticker_id, newRating)}
                                size="sm"
                              />
                            ) : (
                              <span title="Rating unavailable">-</span>
                            )}
                          </td>
                          <td className="text-center" style={{ width: '60px', fontSize: '1rem' }} onClick={e => e.stopPropagation()}>
                            <span
                              role="button"
                              tabIndex={0}
                              className="text-muted"
                              style={{ cursor: 'pointer', display: 'inline-block', minWidth: '60px' }}
                              onClick={() => {
                                setPlanModalItem(item);
                                setPlanModalValue(item.plan || '');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setPlanModalItem(item);
                                  setPlanModalValue(item.plan || '');
                                }
                              }}
                            >
                              {item.plan || '-'}
                            </span>
                          </td>
                          <td className={`text-end ${gainClass(item.gain)}`} style={{ width: '60px' }}>{formatGainPct(item.gain_pct)}</td>
                          <td className="text-center" style={{ width: '60px' }}>
                            {item.is_purchased ? (
                              <FaCheckCircle color="#28a745" size="1.2em" title="Owned" />
                            ) : (
                              <FaEye color="#007bff" size="1.2em" title="Watching" />
                            )}
                          </td>
                          <td className="text-center" style={{ width: '90px' }} onClick={e => e.stopPropagation()}>
                            <Form.Check
                              type="switch"
                              id={`watchlist-price-toggle-${item.id}`}
                              checked={item.is_purchased ? true : item.include_in_price_updates !== false}
                              disabled={item.is_purchased || globalPriceUpdateLoading}
                              onChange={(e) => {
                                e.stopPropagation();
                                handlePriceUpdateToggle(item, e.target.checked);
                              }}
                              title={
                                item.is_purchased
                                  ? 'Active holdings always receive price updates.'
                                  : 'Toggle price updates for this ticker.'
                              }
                            />
                          </td>
                          <td className="text-end align-middle" style={{ width: '180px' }} onClick={e => e.stopPropagation()}>
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="btn-sm me-1"
                              style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #28a745', backgroundColor: 'transparent' }}
                              onClick={() => handlePromote(item.id)}
                              disabled={deletingId === item.id}
                              title="Promote to Readylist"
                            >
                              {deletingId === item.id ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              ) : (
                                <FaArrowUp size="0.8em" />
                              )}
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              className="btn-sm me-1"
                              style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #ffc107', backgroundColor: 'transparent' }}
                              onClick={() => handlePromoteToLeaders(item.id)}
                              disabled={deletingId === item.id}
                              title="Promote to Leaders"
                            >
                              {deletingId === item.id ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              ) : (
                                <FaStar size="0.8em" />
                              )}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="btn-sm"
                              style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #dee2e6', backgroundColor: 'transparent' }}
                              onClick={() => handleRemove(item.id)}
                              disabled={deletingId === item.id}
                              title="Remove"
                            >
                              {deletingId === item.id ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              ) : (
                                <i className="bi bi-trash text-muted"></i>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 'bold', background: '#f8f9fa' }}>
                        <td colSpan={9} className="text-end watchlist-total-row">TOTAL ({prioritizedWatchlist.length}/{maxWatchlistSize})</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* Delete Confirmation Modal */}
          <Modal show={showDeleteConfirm} onHide={handleDeleteCancel} centered>
            <Modal.Header closeButton>
              <Modal.Title>Delete {deleteTicker?.ticker} from Watchlist?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to remove <b>{deleteTicker?.ticker}</b> from this theme?
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleDeleteCancel}>Cancel</Button>
              <Button variant="danger" onClick={handleDeleteConfirm} disabled={deletingId === deleteTicker?.id}>
                {deletingId === deleteTicker?.id ? 'Deleting...' : 'Delete'}
              </Button>
            </Modal.Footer>
          </Modal>
          {/* Order Dialog Modal */}
          {showOrderDialog && (
            <OrderDialog
              key={`order-dialog-${orderAccountId}`}
              show={showOrderDialog}
              onHide={handleOrderDialogClose}
              onSave={handleOrderSave}
              accounts={enhancedAccounts}
              initialAccountId={orderAccountId}
              setAccountId={handleAccountChange}
              ticker={orderTicker}
              tickers={selectedAccountTickers}
              availableTickers={orderTicker ? [{ symbol: orderTicker }] : []}
              availableCash={availableCashAfterOrders}
              loading={orderDialogLoading}
            />
          )}
          {planModalItem && (
            <Modal show={!!planModalItem} onHide={() => setPlanModalItem(null)} centered>
              <Modal.Header closeButton>
                <Modal.Title>Edit Plan for {planModalItem.ticker}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form.Group controlId="planSelect">
                  <Form.Label>Plan</Form.Label>
                  <div className="d-flex flex-column gap-2">
                    {PLAN_OPTIONS.map(option => (
                      <Button
                        key={option}
                        variant={planModalValue === option ? 'primary' : 'outline-secondary'}
                        className="text-start"
                        onClick={async () => {
                          if (!planModalItem || planUpdatingId === planModalItem?.id) return;
                          setPlanModalValue(option);
                          const success = await handlePlanChange(planModalItem, option);
                          if (success) {
                            setPlanModalItem(null);
                          }
                        }}
                        disabled={planUpdatingId === planModalItem?.id}
                      >
                        {option}
                      </Button>
                    ))}
                  </div>
                </Form.Group>
                {planError && <div className="text-danger small mt-2">{planError}</div>}
              </Modal.Body>
            </Modal>
          )}
          
        </div>
        <div className="watchlist-notes-col">
          <TickerNotesPanel
            selectedItem={watchlist.find(w => w.id === selectedId)}
            getTickerIdForItem={getTickerIdForWatchlistItem}
            onNoteChange={fetchWatchlist}
          />
        </div>
      </div>
    </div>
  );
};

export default GLeaderDetails; 
